import './common/common-fields.scss';
import './PaymentStep.scss';

import React from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { faCreditCard } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CardElement } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';

import { PatientOrderingPricing } from '@customTypes/test-ordering';

import { useFetchPaymentIntent } from '../hooks/useFetchPaymentIntent';
import AddressFormFields from './common/AddressFormFields';
import { CheckboxField } from './common/CheckboxField';
import FieldLabel from './common/FieldLabel';

type Address = { street?: string; street2?: string; city?: string; state?: string; zip?: string };

type Props = {
  values: {
    paymentIntentId?: string;
    promoCode?: string;
    useShippingForBilling?: boolean;
    billingAddress?: Address;
    address?: Address; // shipping address for prefill
    confirmedBillingAddress?: {
      useShippingForBilling: boolean;
      address?: Address;
    };
  };
  showErrors: boolean;
  errors: Record<string, string>;
  update: (path: string, val: unknown) => void;
  stripeError?: string;
  stripe: Stripe | null;
  elements: StripeElements | null;
  pricing: PatientOrderingPricing | undefined;
};

export default function PaymentStep({
  values,
  pricing,
  showErrors,
  errors,
  update,
  stripeError,
  stripe,
  elements,
}: Props) {
  useFetchPaymentIntent({ promoCode: values.promoCode });

  // Only show loading spinner if paymentInfo is missing (initial load or refetch)
  // Don't unmount CardElement when loading is true during confirmation
  if (!pricing) {
    return (
      <div className='wizard-loading'>
        <Spinner animation='border' role='status' aria-label='Loading payment information' />
        <div className='wizard-loading__caption'>Loading payment information…</div>
      </div>
    );
  }

  const hasDiscount = !!pricing.discount;
  const isPaymentConfirmed = !!values.paymentIntentId;

  // Helper to get the confirmed billing address to display
  const getConfirmedBillingAddress = (): { useShippingForBilling: boolean; address?: Address } | null => {
    // Priority 1: Use confirmed billing address if available
    if (values.confirmedBillingAddress) {
      const address = values.confirmedBillingAddress.useShippingForBilling
        ? values.confirmedBillingAddress.address || values.address
        : values.confirmedBillingAddress.address;

      if (address) {
        return {
          useShippingForBilling: values.confirmedBillingAddress.useShippingForBilling,
          address,
        };
      }
    }

    // Priority 2: Fallback to current values if payment is confirmed but no confirmed address
    if (isPaymentConfirmed) {
      const address = values.useShippingForBilling ? values.address : values.billingAddress;
      if (address) {
        return {
          useShippingForBilling: values.useShippingForBilling ?? true,
          address,
        };
      }
    }

    return null;
  };

  const confirmedBilling = getConfirmedBillingAddress();

  return (
    <div className='form-grid'>
      {/* Pricing Breakdown */}
      <div className='field field-span-2'>
        <div className='po-field'>
          <FieldLabel>Order Summary</FieldLabel>
          <div className='po-order-summary-box'>
            <table className='table table-borderless bg-transparent po-order-summary-table'>
              <tbody>
                <tr>
                  <th>Subtotal</th>
                  <td className='text-end'>${Number(pricing.original_price_cents / 100).toFixed(2)}</td>
                </tr>

                {hasDiscount && (
                  <tr>
                    <th>
                      Discount
                      <div className='text-muted small'>{pricing.promo_code}</div>
                    </th>
                    <td className='text-end text-danger'>-${Number(pricing.discount_cents / 100).toFixed(2)}</td>
                  </tr>
                )}

                <tr className='border-top'>
                  <th>Total</th>
                  <td className='text-end fw-bold'>${Number(pricing.price_cents / 100).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Card Element */}
      <div className='field field-span-2'>
        <div className='po-field'>
          <FieldLabel required>Card Information</FieldLabel>
          {values.paymentIntentId ? (
            // Show "Card Info Saved" message when payment is confirmed
            <div className='bg-white rounded-3 d-flex flex-column po-payment-card-block'>
              <div className='d-flex align-items-center mb-2'>
                <FontAwesomeIcon icon={faCreditCard} className='me-2 po-payment-card-icon' color='primary' />
                <p className='text-primary fs-lg fw-bold mb-0 po-payment-card-block__title'>Card Info Saved</p>
              </div>
              <p className='text-secondary fs-sm d-inline-block'>
                You won&apos;t be charged until your order has been reviewed by one of our clinical experts.
              </p>
            </div>
          ) : stripe && elements ? (
            <>
              <div className={`po-stripe-card-wrapper ${stripeError ? 'po-stripe-card-wrapper--error' : ''}`}>
                <CardElement className='po-stripe-card__element' options={{}} />
              </div>
              {stripeError ? <div className='po-error inline'>{stripeError}</div> : null}
              <p className='po-payment-step__note mb-0'>
                Your card will not be charged until your order has been approved by one of our clinical experts.
              </p>
              <p className='po-payment-step__note mb-2'>We also accept HSA/FSA payments</p>
            </>
          ) : (
            <div className='bg-white rounded-3 d-flex flex-column po-payment-card-block'>
              <div className='d-flex align-items-center mb-3'>
                <p className='text-primary fs-lg fw-bold mb-0 po-payment-card-block__title'>
                  Credit Card Entry Unavailable
                </p>
              </div>
              <p className='text-secondary d-inline-block fs-sm'>
                Please try disabling your extensions to enable credit card entry.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Billing Address Section */}
      {isPaymentConfirmed && confirmedBilling ? (
        /* Read-only billing address display after payment confirmation */
        <div className='field field-span-2'>
          <div className='po-field'>
            <FieldLabel>
              {confirmedBilling.useShippingForBilling ? 'Billing Address (Using Shipping Address)' : 'Billing Address'}
            </FieldLabel>
            <div className='po-billing-confirmed-box'>
              {confirmedBilling.useShippingForBilling ? (
                <Stack gap={2}>
                  {confirmedBilling.address?.street && <div>{confirmedBilling.address.street}</div>}
                  {confirmedBilling.address?.street2 && <div>{confirmedBilling.address.street2}</div>}
                  <div>
                    {confirmedBilling.address?.city && <span>{confirmedBilling.address.city}, </span>}
                    {confirmedBilling.address?.state && <span>{confirmedBilling.address.state} </span>}
                    {confirmedBilling.address?.zip && <span>{confirmedBilling.address.zip}</span>}
                  </div>
                </Stack>
              ) : confirmedBilling.address ? (
                <Stack gap={2}>
                  {confirmedBilling.address.street && <div>{confirmedBilling.address.street}</div>}
                  {confirmedBilling.address.street2 && <div>{confirmedBilling.address.street2}</div>}
                  <div>
                    {confirmedBilling.address.city && <span>{confirmedBilling.address.city}, </span>}
                    {confirmedBilling.address.state && <span>{confirmedBilling.address.state} </span>}
                    {confirmedBilling.address.zip && <span>{confirmedBilling.address.zip}</span>}
                  </div>
                </Stack>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Billing Address Toggle (only shown before payment confirmation) */}
          <div className='field field-span-2'>
            <CheckboxField
              id='useShippingForBilling'
              label='Use shipping address for billing'
              checked={values.useShippingForBilling ?? true}
              onChange={val => update('useShippingForBilling', val)}
            />
          </div>

          {/* Billing Address Fields (shown when toggle is off) */}
          {!values.useShippingForBilling ? (
            <AddressFormFields
              fieldPrefix='billingAddress'
              labelPrefix='Billing'
              address={values.billingAddress || {}}
              update={update}
              showErrors={showErrors}
              errors={errors}
              containerClassName='field-span-2'
            />
          ) : null}
        </>
      )}
    </div>
  );
}
